/**
 * URLs for the assignment-quality pages.
 *
 * The assignment key is (questionSetId, assignedById, batchId): the set goes in
 * the path, the rest in the query, mirroring the API. One place builds them so
 * a link can't drop `by` and silently address a different teacher's class.
 */

import type { AssignmentKey } from '../../../types/assignmentQuality';

function keyQuery(key: AssignmentKey): string {
    const qs = new URLSearchParams({ by: key.assignedById });
    if (key.batchId) qs.set('batchId', key.batchId);
    return `?${qs.toString()}`;
}

export function assignmentPath(key: AssignmentKey): string {
    return `/evals/assignments/${encodeURIComponent(key.questionSetId)}${keyQuery(key)}`;
}

/** Every analysis the assignment produced. `questionId` / `studentId` arrive as
 *  pre-selected FILTERS — the page is the assignment, not the question. */
export function assignmentAnalysesPath(
    key: AssignmentKey,
    pre: { questionId?: string; studentId?: string; quality?: string } = {},
): string {
    const qs = new URLSearchParams({ by: key.assignedById });
    if (key.batchId) qs.set('batchId', key.batchId);
    if (pre.questionId) qs.set('questionId', pre.questionId);
    if (pre.studentId) qs.set('studentId', pre.studentId);
    if (pre.quality) qs.set('quality', pre.quality);
    return `/evals/assignments/${encodeURIComponent(key.questionSetId)}/analyses?${qs.toString()}`;
}

/** The question itself, in the Questions console. Opened in a new tab from
 *  these tables — an admin checking what a question actually says is not done
 *  with the analyses they were reading. */
export function questionDetailPath(questionId: string): string {
    return `/questions?id=${encodeURIComponent(questionId)}`;
}

/** The student's admin record. Opened in a new tab for the same reason as a
 *  question: checking who someone is shouldn't cost the analyses on screen. */
export function studentDetailPath(studentId: string): string {
    return `/users/${encodeURIComponent(studentId)}`;
}

/** The full analysis behind one submission — the existing Operations detail
 *  page, which already renders every step, its prompt and its raw output. */
export function analysisDetailPath(submissionId: string): string {
    return `/submissions/${encodeURIComponent(submissionId)}`;
}

/** The student page is about the STUDENT, not the assignment — the question-set
 *  filter arrives as a droppable chip, not as part of its identity. */
export function studentAnalysesPath(studentId: string, questionSetId?: string): string {
    const qs = questionSetId ? `?questionSetId=${encodeURIComponent(questionSetId)}` : '';
    return `/evals/students/${encodeURIComponent(studentId)}${qs}`;
}
